/**
 * Constants shared between the app-level SessionService (owns the auth
 * expiry lifecycle) and libs that need to signal intent into it — most
 * notably the header's "Log out" menu, which cannot import SessionService
 * directly because libs/shared can't depend on apps/.
 *
 * Keep this file free of runtime dependencies so both sides can consume
 * it without pulling in unrelated code.
 */

/**
 * sessionStorage key written by any UI that initiates a deliberate
 * sign-out BEFORE calling auth.logout(). SessionService's
 * isAuthenticated$ watcher checks this flag when the SDK's state flips
 * false — if set, the transition is user-initiated and the expired
 * dialog is suppressed. Cleared by SessionService once observed.
 *
 * The stored value is a JSON string `{"ts": <epoch-ms>}` rather than a
 * raw `'1'` so we can enforce a short TTL. Without one, a logout redirect
 * blocked by CSP / an extension / a network failure would leave the flag
 * permanently set, and the NEXT genuine expiry in this tab would be
 * silently swallowed (dialog never appears, user stuck receiving 401s).
 */
export const DELIBERATE_LOGOUT_KEY = 'kaboom.deliberate-logout';

/**
 * Maximum age (ms) a deliberate-logout flag is considered valid. A real
 * logout redirect completes in well under this window; any flag older
 * than this is treated as stale (blocked redirect leftover) and ignored.
 */
export const DELIBERATE_LOGOUT_TTL_MS = 30_000;

/**
 * Shape of the JSON blob stored under DELIBERATE_LOGOUT_KEY.
 */
export interface DeliberateLogoutFlag {
  ts: number;
}
